/**
 * `schemas/queue.ts` — the SET LIST, `spec.md` §6.7.
 *
 * `queue_items.position` is a `REAL` and the unique index is on
 * `(user_id, track_id, position)`, NOT `(user_id, track_id)` — a selector may queue the
 * same tune twice. Nothing in these schemas may assume `trackIds` is a set.
 */
import * as z from 'zod';
import { PositiveIntParam, RowId, TrackId } from './common.js';
import { TrackSummarySchema } from './track.js';
/** `PUT /api/queue` bulk-inserts with `position = i * 1024` (§6.7). */
export const QUEUE_MAX_ITEMS = 1000;
/**
 * The current index. `-1` is legal and means "nothing selected" — an empty queue, or a
 * queue whose current item was just removed.
 */
export const QueueIndex = z.int().min(-1);
export const QueueItemSchema = z.strictObject({
    /** `queue_items.id` — the handle for `DELETE /api/queue/items/:id` and reorder. */
    id: RowId,
    /** `REAL`; the midpoint algorithm produces non-integers immediately. */
    position: z.number(),
    played: z.boolean(),
    track: TrackSummarySchema,
});
export const QueueResponseSchema = z.strictObject({
    items: z.array(QueueItemSchema).max(QUEUE_MAX_ITEMS),
    index: QueueIndex,
    durationRemainingS: z.int().nonnegative(),
});
/**
 * The lightweight header returned by the mutations that do not need to ship the whole
 * list back — §6.7 calls it "the new queue header" for
 * `DELETE /api/queue/items/:id`.
 */
export const QueueHeaderSchema = z.strictObject({
    count: z.int().nonnegative(),
    index: QueueIndex,
    durationRemainingS: z.int().nonnegative(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * PUT /api/queue — replace the whole queue ("play all", "play this playlist")
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * `trackIds` may be EMPTY (`0..1000`): replacing the queue with nothing is how the
 * client clears it in one round trip. `index`, when given, must point inside the list.
 */
export const QueueReplaceRequestSchema = z
    .strictObject({
    trackIds: z.array(TrackId).max(QUEUE_MAX_ITEMS),
    index: z.int().nonnegative().optional(),
})
    .refine((body) => body.index === undefined || body.index < body.trackIds.length, {
    error: 'index must point at an item in trackIds',
    path: ['index'],
});
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/queue/items
 * ──────────────────────────────────────────────────────────────────────────── */
export const QUEUE_INSERT_MODES = ['next', 'end'];
/** `next` inserts at the midpoint between the current index and the following item. */
export const QueueInsertMode = z.enum(QUEUE_INSERT_MODES);
export const QueueAddItemsRequestSchema = z.strictObject({
    trackIds: z.array(TrackId).min(1).max(200),
    mode: QueueInsertMode,
});
/* ────────────────────────────────────────────────────────────────────────────
 * DELETE /api/queue/items/:id
 * ──────────────────────────────────────────────────────────────────────────── */
export const QueueItemParamsSchema = z.object({ id: PositiveIntParam });
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/queue/reorder
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Keyed by `queue_items.id`, not by track id — the same track may occupy two rows, so a
 * track id would be ambiguous. `beforeItemId: null` moves the item to the end.
 */
export const QueueReorderRequestSchema = z
    .strictObject({
    itemId: RowId,
    beforeItemId: RowId.nullable(),
})
    .refine((body) => body.beforeItemId !== body.itemId, {
    error: 'an item cannot be reordered before itself',
    path: ['beforeItemId'],
});
//# sourceMappingURL=queue.js.map